import apiClient from "./apiClient";
import { ENDPOINTS } from "./endpoints";

export type ListenerType = "Volunteer" | "Professional" | (string & {});

export type ListenerUser = {
  id: string;
  _id?: string;
  name: string;
  points?: number;
  avatar?: string;
  listenerType?: string;
};

export type ListenerListQuery = {
  listenerType?: string;
  q?: string;
  limit?: number;
};

export async function getListeners(query?: ListenerListQuery) {
  const params = new URLSearchParams();
  if (query?.listenerType) params.set("listenerType", query.listenerType);
  if (query?.q) params.set("q", query.q);
  if (query?.limit != null) params.set("limit", String(query.limit));

  const suffix = params.toString() ? `?${params.toString()}` : "";
  const res = await apiClient.get(`${ENDPOINTS.USERS.LISTENERS}${suffix}`);
  return res.data as { listeners: ListenerUser[] } | ListenerUser[];
}

export async function getListener(listenerId: string) {
  const res = await apiClient.get(ENDPOINTS.USERS.LISTENER(listenerId));
  return res.data as { listener: ListenerUser } | ListenerUser;
}

export type UserProfile = {
  _id: string;
  name: string;
  email: string;
  role: string;
  avatar: string;
  points: number;
  isPremium: boolean;
  freeTrialUsed: boolean;
  freeTrialMinutes: number;
  listenerType: string | null;
  bio?: string;
  skills: string[];
  languages: string[];
  gender: "male" | "female" | "other";
  kycStatus: "none" | "pending" | "verified" | "rejected";
  kycDocumentUrl?: string;
  sessionsCompleted: number;
  isLateNightListener: boolean;
  createdAt: string;
  updatedAt: string;
};

export async function getProfile(forceRefresh = true) {
  if (!forceRefresh) {
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && (parsed._id || parsed.id || parsed.email)) {
          return { success: true, user: parsed as UserProfile };
        }
      }
    } catch {
      // If parsing fails, proceed to API call
    }
  }

  try {
    const res = await apiClient.get(ENDPOINTS.USERS.PROFILE);
    if (res.data?.success && res.data?.user) {
      try {
        const existing = localStorage.getItem("user");
        const existingUser = existing ? JSON.parse(existing) : {};
        const mergedUser = { ...existingUser, ...res.data.user };
        localStorage.setItem("user", JSON.stringify(mergedUser));
      } catch {
        localStorage.setItem("user", JSON.stringify(res.data.user));
      }
      return res.data as { success: boolean; user: UserProfile };
    }
    return res.data;
  } catch (error) {
    const stored = localStorage.getItem("user");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed && (parsed._id || parsed.id || parsed.email)) {
          return { success: true, user: parsed as UserProfile };
        }
      } catch {
        // ignore
      }
    }
    throw error;
  }
}

export async function updateProfile(
  data: Partial<{
    name: string;
    avatar: string;
    email: string;
    listenerType: string;
    bio: string;
    skills: string[];
    languages: string[];
    gender: string;
    isLateNightListener: boolean;
  }>,
) {
  const res = await apiClient.patch(ENDPOINTS.USERS.PROFILE, data);
  if (res.data?.success && res.data?.user) {
    try {
      const existing = localStorage.getItem("user");
      const existingUser = existing ? JSON.parse(existing) : {};
      const mergedUser = { ...existingUser, ...res.data.user };
      localStorage.setItem("user", JSON.stringify(mergedUser));
    } catch {
      localStorage.setItem("user", JSON.stringify(res.data.user));
    }
  }
  return res.data as { success: boolean; user: UserProfile };
}

export async function submitKyc(documentUrl: string) {
  const res = await apiClient.post(`${ENDPOINTS.USERS.PROFILE}/kyc`, {
    documentUrl,
  });
  if (res.data?.success) {
    try {
      const existing = localStorage.getItem("user");
      if (existing) {
        const parsed = JSON.parse(existing);
        parsed.kycStatus = res.data.kycStatus || "pending";
        localStorage.setItem("user", JSON.stringify(parsed));
      }
    } catch {}
  }
  return res.data as { success: boolean; message: string; kycStatus: string };
}

export type UserStats = {
  totalSessions: number;
  hoursOfSupport: number;
  totalPoints: number;
  totalEarnings: number;
  moodScore: string;
  peopleHelped?: number;
  empathyScore?: string;
  anxietyRelieved?: number;
  stressReduced?: number;
  joyRestored?: number;
};

export async function getUserStats() {
  const res = await apiClient.get(
    `${ENDPOINTS.USERS.PROFILE?.replace("/profile", "/stats") || "/users/stats"}`,
  );
  return res.data as { success: boolean; stats: UserStats };
}

export async function subscribePush(subscription: any) {
  const res = await apiClient.post(ENDPOINTS.USERS.SUBSCRIBE_PUSH, {
    subscription,
  });
  return res.data as { success: boolean; message: string };
}

export type WithdrawalRequest = {
  _id: string;
  listener:
    | string
    | { _id: string; name: string; email: string; avatar?: string };
  amount: number;
  payoutMethod: "upi" | "bank";
  upiId?: string;
  bankDetails?: {
    accountHolder?: string;
    accountNumber?: string;
    ifsc?: string;
    bankName?: string;
  };
  status: "pending" | "approved" | "rejected" | "processed";
  adminNote?: string;
  transactionRef?: string;
  processedAt?: string;
  createdAt: string;
};

export async function requestWithdrawal(data: {
  amount: number;
  payoutMethod: "upi" | "bank";
  upiId?: string;
  bankDetails?: {
    accountHolder?: string;
    accountNumber?: string;
    ifsc?: string;
    bankName?: string;
  };
}) {
  const res = await apiClient.post(ENDPOINTS.USERS.WITHDRAW, data);
  return res.data as {
    success: boolean;
    message: string;
    withdrawal: WithdrawalRequest;
  };
}

export async function getListenerWithdrawals() {
  const res = await apiClient.get(ENDPOINTS.USERS.WITHDRAWALS);
  return res.data as {
    success: boolean;
    withdrawals: WithdrawalRequest[];
    minWithdrawalAmount: number;
  };
}

export async function submitIdentityVerificationApi(payload: {
  idType: string;
  frontImage: string;
  frontFileName?: string;
  backImage?: string | null;
  backFileName?: string;
  selfieImage: string;
}) {
  const res = await apiClient.post("/users/verification", payload);
  return res.data as {
    success: boolean;
    message: string;
    verification: any;
  };
}

export async function getIdentityVerificationStatusApi() {
  const res = await apiClient.get("/users/verification");
  return res.data as {
    success: boolean;
    kycStatus: string;
    verification: any;
  };
}
